import React from 'react';

import { throwCustomAquaError } from 'a/components/GlobalErrorHandler/actions';
import { ERROR_STRINGS } from 'a/components/GlobalErrorHandler/constants';
import InlineSvgStr from 'a/components/InlineSvgStr';
import store from 'a/store';
import { LOCALIZATIONS } from 'root/lib/constants';
import Env from 'lib/Env';
import { verifyLocalizedAssetOnRemote } from 'lib/StaticAssetsManifest';
import { aquaAxios } from 'lib/utilityFunctions';
import { getLocalization } from 'a/selectors/languageSelectors';

const { GAME_CONTENT_URL, isDevEnv } = Env;

/**
 * new and simplified AssetLoader that doesn't rely on instantiating a class.
 * this version of the AssetLoader should be preferred over the old version and
 * should be expanded as necessary to cover use cases as they're uncovered.
 *
 * all asset fetching functionality is kept private to this module. the StaticAssetsManifest is currently still separate
 * but could be folded into this module as well.
 */

/**
 * an object describing a specific asset. must include a name, filename, and
 * path. may include other optional data to describe the asset, provide
 * accessibility data, etc.
 * @typedef {Object} AssetObject
 * @property {string} name name of the asset
 * @property {string} file filename (with extension) of the asset
 * @property {string} path remote directory path to find asset
 */

let assetCache = {
  /*
  'foo/bar/en/baz.mp3': '...',
  */
};

// caches captions for audio assets. will cache with country code for easy
// retrieval based on language
let captionCache = {
  /*
  'foo/bar/en/baz.mp3': '...',
  */
};

// caches alt text for image assets. will cache with country code for easy
// retrieval based on language
let altCache = {
  /*
  'foo/bar/en/baz.mp3': '...',
  */
};

/**
 * selector for country code, which can also take licensing into account.
 * @returns {string} country code (e.g. "en")
 */
const getLocalizationCode = () => getLocalization(store.getState());

/**
 * gets an asset from the cache given its identifying AssetObject.
 * @param {AssetObject} assetObject object containing the name, path, etc. for an asset
 * @returns specified asset
 */
export const getAssetFromCache = ({ file, path } = {}) => {
  if (!file || !path) {
    const message = 'asset loading error: invalid input';
    const missingAsset = 'printing missing asset: invalid input';
    if (isDevEnv) {
      console.error(message);
      console.error(missingAsset);
    }
    return undefined;
  }
  const localizationCode = getLocalizationCode();
  const filename = `${path}${localizationCode}/${file}`;

  const asset = assetCache[filename];

  if (!asset) {
    const message = 'asset loading error: asset not found in cache';
    const missingAsset = `printing missing asset: ${filename}`;
    if (isDevEnv) {
      console.error(message);
      console.error(missingAsset);
    }
    throwGlobalError(`${message}; ${missingAsset}`);
  }

  return asset;
};

/**
 * gets a caption from the cache given its identifying AssetObject.
 * @param {AssetObject} assetObject object containing the name, path, etc. for an asset
 * @returns specified caption
 */
export const getCaptionFromCache = ({ file, path }) => {
  const localizationCode = getLocalizationCode();
  const filename = `${path}${localizationCode}/${file}`;

  const caption = captionCache[filename];

  if (!caption) {
    const message = 'asset loading error: caption not found in cache';
    const missingAsset = `printing missing caption: ${filename}`;
    if (isDevEnv) {
      console.warn(message);
      console.warn(missingAsset);
    }
  }

  return caption;
};

/**
 * gets alt text from the cache given its identifying AssetObject.
 * @param {AssetObject} assetObject object containing the name, path, etc. for an asset
 * @returns specified alt text
 */
export const getAltFromCache = ({ file, path }) => {
  const localizationCode = getLocalizationCode();
  const filename = `${path}${localizationCode}/${file}`;

  const alt = altCache[filename];

  if (!alt) {
    const message = 'asset loading error: alt text not found in cache';
    const missingAsset = `printing missing alt text: ${filename}`;
    if (isDevEnv) {
      console.warn(message);
      console.warn(missingAsset);
    }
  }

  return alt;
};

/**
 * verifies that a localized version of an asset exists; if false, asset should
 * default to the English version.
 * @param {AssetObject} assetObject object containing the name, path, etc. for an asset
 * @returns {string} verified filename for the asset object
 */
const verifyLocalizedAsset = ({ file, path }, localizationCode = getLocalizationCode()) => {
  const filename = `${path}${localizationCode}/${file}`;
  if (localizationCode !== LOCALIZATIONS.EN) {
    // check if localized asset is available
    const isAssetVerified = verifyLocalizedAssetOnRemote(filename);
    // if we're in lectura and there's no asset, trying fallbacking to ES first by running this again
    if (!isAssetVerified && localizationCode === LOCALIZATIONS.SPA_ES_DEFAULT) {
      return verifyLocalizedAsset({ file, path }, LOCALIZATIONS.ES);
    }
    return isAssetVerified ? filename : `${path}${LOCALIZATIONS.EN}/${file}`;
  }
  return filename;
};

/**
 * fetches the audio file at the given filename as a base 64 string.
 * @param {string} url full address for the audio file
 * @returns {string} audio file as a base 64 string
 */
const fetchAudio = async (url) => {
  try {
    const { data } = await aquaAxios(url, { responseType: 'arraybuffer', timeout: 0 });
    const buffer = data;
    // String.fromCharCode overloads if it is more then 64 kb
    const byteLimit = 63000;
    const batches = buffer.byteLength / byteLimit;
    let base64String = '';
    let length = 0;
    // convert to base64
    for (let i = 0; i < batches; i++) {
      length = byteLimit;
      // last run
      if (i + 1 > batches) {
        length = buffer.byteLength - (i * byteLimit);
      }
      base64String += btoa(String.fromCharCode(...new Uint8Array(buffer, i * byteLimit, length)));
    }
    return `data:audio/mpeg;base64,${base64String}`;
  } catch (error) {
    const message = 'asset loading error: unable to create base 64 string';
    const missingAsset = `printing problematic asset: ${url}`;
    if (isDevEnv) {
      console.error(message);
      console.error(missingAsset);
    }
    throwGlobalError(`${message}; ${missingAsset}`);
    return undefined;
  }
};

/**
 * fetches json at the given filename.
 * @param {string} url full address for json
 * @return {object} json
 */
const fetchJson = async (url) => {
  try {
    const { data } = await aquaAxios(url, { timeout: 0 });
    return data;
  } catch (error) {
    const message = 'asset loading error: unable to fetch json';
    const missingAsset = `printing problematic asset: ${url}`;
    if (isDevEnv) {
      console.error(message);
      console.error(missingAsset);
    }
    throwGlobalError(`${message}; ${missingAsset}`);
    return undefined;
  }
};

/**
 * fetches the svg at the given filename as a string and then converts to an
 * inline component for storage.
 * @param {string} url full address for the svg
 * @returns {React.Component} <InlineSvgStr />
 */
const fetchSvg = async (url) => {
  try {
    const { data } = await aquaAxios({ url, responseType: 'text', timeout: 0 });
    return React.forwardRef(
      (props, ref) => <InlineSvgStr {...props} asset={data} ref={ref} />,
    );
  } catch (error) {
    const message = 'asset loading error: unable to fetch svg';
    const missingAsset = `printing problematic asset: ${url}`;
    if (isDevEnv) {
      console.error(message);
      console.error(missingAsset);
    }
    throwGlobalError(`${message}; ${missingAsset}`);
    return undefined;
  }
};

/**
 * fetches a given asset's data from AWS. if the asset is already cached,
 * returns the cached asset instead.
 * @param {AssetObject} asset object containing the name, path, etc. for an asset
 * @returns {Promise} promise that resolves in the asset data to store in cache
 */
const fetchAsset = (asset) => {
  const localizationCode = getLocalizationCode();
  const { file, path } = asset;
  const filename = `${path}${localizationCode}/${file}`;

  // if the asset already exists in the cache, just return that instead
  if (assetCache[filename]) {
    return assetCache[filename];
  }

  // if a localized asset (e.g. a Spanish asset) doesn't exit, instead use an
  // English asset
  const url = `${GAME_CONTENT_URL}/${verifyLocalizedAsset(asset)}`;
  const ext = url.split('.').pop().toLowerCase();
  switch (ext) {
    case 'json':
      return fetchJson(url);
    case 'svg':
      return fetchSvg(url);
    case 'mp3':
    default: {
      return fetchAudio(url);
    }
  }
};

/**
 * cache data related to a specific AssetObject.
 * @param {AssetObject} assetObject object containing the name, path, etc. for an asset
 * @param {any} data data to store for the specified AssetObject
 */
const cacheAsset = ({ file, path, captions, alt }, data) => {
  const localizationCode = getLocalizationCode();
  const filename = `${path}${localizationCode}/${file}`;

  assetCache[filename] = data;

  if (captions) {
    captionCache[filename] = captions[localizationCode];
  }

  if (alt) {
    altCache[filename] = alt[localizationCode];
  }
};

/**
 * uses an array of AssetObjects to load assets of different types from AWS to
 * the assetCache. AssetObjects are saved in usable forms, so some types of
 * assets, like SVGs, are saved as components.
 * @param {Array<AssetObject>} assets array containing at least one AssetObject
 * @param {function} [onComplete] callback for when the assets are loaded
 * @returns {bool} are the assets currently being loaded (true), or have the assets finished loading (false)?
 */
export const loadAssets = async (assets, onComplete) => {
  // if the assets are not presented in a single array, throw an error
  if (!Array.isArray(assets)) {
    const message = 'asset loading error: did not recieve an array of objects to load';
    console.error(message);
    throwGlobalError(message);
    return false;
  }

  // create array of promises and wait for them to be fulfilled
  const promises = [];
  assets.forEach((asset) => {
    const { hasAnimationFrames } = asset;
    if (hasAnimationFrames) {
      promises.push(fetchAsset({
        name: asset.file.replace('.json', '.frames.json'),
        file: asset.file.replace('.json', '.frames.json'),
        path: asset.path,
      }));
    }
    promises.push(fetchAsset(asset));
  });

  // after fulfilling promises, populate the cache
  await Promise.all(promises).then((data) => {
    data.forEach((asset, i) => {
      cacheAsset(assets[i], asset);
    });
  });

  if (onComplete) {
    onComplete();
  }

  return false;
};

/**
 * empty assetCache of all data.
 */
export const clearAssetCache = () => {
  assetCache = {};
  captionCache = {};
  altCache = {};
};

const defaultErrorMessage = 'Failed to download your assets; showing global error screen';

/**
 * Dispatch action to show the global Alyx error page with "Something went wrong."
 */
const throwGlobalError = (errorMessage = defaultErrorMessage, errorName = 'AssetLoader Error') => {
  store.dispatch(throwCustomAquaError({
    titleText: ERROR_STRINGS.uhOh,
    secondaryText: ERROR_STRINGS.theresBeenAnError,
    showGamesButton: false,
    showHomeButton: true,
    forceLogout: false,
    errorMessage,
    errorName,
    category: 'Resource: AssetLoader',
    metricId: 'resource',
  }));
};
